/**
 * JSON to HTML Converter Utility
 * 
 * This utility converts contract template JSON data into HTML form elements.
 * It handles various section types including headings, paragraphs, lists, tables,
 * and mixed content with form inputs.
 */

/**
 * Converts markdown styling to HTML tags
 * @param {string} text - Text containing markdown styling
 * @returns {string} - HTML with markdown converted to tags
 */
const convertMarkdownToHtml = (text) => {
  if (!text) return '';

  // Protect variable placeholders from markdown processing
  const varTokens = {};
  let protectedText = text.replace(/\{\{[^}]+\}\}/g, (match) => {
    const token = `__VAR_TOKEN_${Object.keys(varTokens).length}__`;
    varTokens[token] = match;
    return token;
  });

  // Convert markdown to HTML
  // Bold: **text** or __text__
  protectedText = protectedText.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  protectedText = protectedText.replace(/__(.*?)__/g, '<strong>$1</strong>');
  
  // Italic: *text* or _text_
  protectedText = protectedText.replace(/\*(.*?)\*/g, '<em>$1</em>');
  protectedText = protectedText.replace(/_(.*?)__/g, '<em>$1</em>');
  
  // Strikethrough: ~~text~~
  protectedText = protectedText.replace(/~~(.*?)~~/g, '<del>$1</del>');
  
  // Underline: <u>text</u> (custom markdown extension)
  protectedText = protectedText.replace(/<u>(.*?)<\/u>/g, '<u>$1</u>');
  
  // Headers: # ## ### etc.
  protectedText = protectedText.replace(/^### (.*$)/gm, '<h3>$1</h3>');
  protectedText = protectedText.replace(/^## (.*$)/gm, '<h2>$1</h2>');
  protectedText = protectedText.replace(/^# (.*$)/gm, '<h1>$1</h1>');

  // Restore variable tokens
  Object.keys(varTokens).forEach(token => {
    protectedText = protectedText.replace(token, varTokens[token]);
  });

  return protectedText;
};

/**
 * Validates and fixes common JSON formatting issues
 * @param {string} jsonString - The JSON string to validate and fix
 * @returns {string} - The fixed JSON string
 */
export const validateAndFixJson = (jsonString) => {
    try {
      JSON.parse(jsonString);
      return jsonString;
    } catch (error) {}
  
    let fixedJson = jsonString;
  
    fixedJson = fixedJson
      .replace(/"\s*}\s*"/g, '" }, "')
      .replace(/"\s*]\s*"/g, '" ], "')
      .replace(/"\s*}\s*{/g, '" }, {')
      .replace(/"\s*]\s*{/g, '" ], {')
      .replace(/"\s*}\s*\[/g, '" }, [')
      .replace(/"\s*]\s*\[/g, '" ], [')
      .replace(/,(\s*[}\]])/g, '$1')
      .replace(/([{,]\s*)([a-zA-Z_][a-zA-Z0-9_]*)\s*:/g, '$1"$2":')
      .replace(/'/g, '"')
      .replace(/[""]/g, '"')
      .replace(/['']/g, '"')
      .replace(/\s+/g, ' ')
      .trim();
  
    try {
      JSON.parse(fixedJson);
      return fixedJson;
    } catch (error) {
      return jsonString;
    }
  };
  
export const convertMixedContentToHtml = (content = []) => {
    return content.map(item => {
      const { type, inputType, name, options, rows } = item;
  
      if (type === 'text') return item.text;
      if (type === 'input') {
        switch (inputType) {
          case 'checkbox':
            return `<input type="checkbox" name="${name}" style="margin: 0 4px; vertical-align: middle;" />`;
          case 'text':
            return `<input type="text" name="${name}" placeholder="Enter text..." style="margin: 0 4px; padding: 4px; border: 1px solid #ccc; border-radius: 4px;" />`;
          case 'textarea':
            return `<textarea name="${name}" placeholder="Enter text..." rows="${rows || 3}" style="margin: 0 4px; padding: 4px; border: 1px solid #ccc; border-radius: 4px; min-width: 200px;"></textarea>`;
          case 'dropdown':
            return `<select name="${name}" style="margin: 0 4px; padding: 4px; border: 1px solid #ccc; border-radius: 4px; min-width: 150px;">${
              options?.map(option => `<option value="${option.value || option}">${option.label || option}</option>`).join('') || ''
            }</select>`;
          case 'date':
            return `<input type="date" name="${name}" style="margin: 0 4px; padding: 4px; border: 1px solid #ccc; border-radius: 4px; min-width: 150px;" />`;
          case 'number':
            return `<input type="number" name="${name}" placeholder="0" style="margin: 0 4px; padding: 4px; border: 1px solid #ccc; border-radius: 4px; min-width: 80px;" />`;
          default:
            return '';
        }
      }
      return '';
    }).join('');
  };
  
export const convertTextWithCheckboxesToHtml = (text = '') => {
  // First convert checkboxes
  let processedText = text.replace(/\[checkbox\]/g, '<input type="radio" style="margin: 0 4px; vertical-align: middle;" />');
  // Then apply markdown processing
  return convertMarkdownToHtml(processedText);
};
  
export const convertTableToHtml = (tableData) => {
  let html = '<div class="table-container">\n<table class="data-table">\n';

  // Check for new table format with 'head' and 'rows'
  if (tableData.head) {
    // New format: head contains markdown table header
    const headText = tableData.head;
    if (headText.startsWith('|')) {
      // Parse markdown table header
      const headerCells = headText.split('|').slice(1, -1).map(cell => cell.trim());
      html += '<thead><tr>' + headerCells.map(cell => `<th>${convertMarkdownToHtml(cell)}</th>`).join('') + '</tr></thead>\n';
    }
  }

  // Check for new table format with 'rows'
  if (tableData.rows) {
    html += '<tbody>';
    tableData.rows.forEach(row => {
      const inputText = row.input;
      if (inputText && inputText.startsWith('|')) {
        // Parse markdown table row
        const cells = inputText.split('|').slice(1, -1).map(cell => cell.trim());
        html += '<tr>' + cells.map(cell => {
          const cellHtml = processTableCellContent(cell);
          return `<td>${cellHtml}</td>`;
        }).join('') + '</tr>';
      }
    });
    html += '</tbody>\n';
  }

  // Fallback to old format
  else if (tableData.thead || tableData.tbody) {
    if (tableData.thead) {
      html += '<thead><tr>' + tableData.thead.tr.map(h => `<th>${convertMarkdownToHtml(h.th)}</th>`).join('') + '</tr></thead>\n';
    }

    if (tableData.tbody) {
      html += '<tbody>' + tableData.tbody.rows.map(row =>
        '<tr>' + row.tr.map(cell => {
          if (cell?.td?.type === 'input') {
            return `<td><input type="${cell.td.inputType || 'text'}" name="${cell.td.name}" class="form-input" /></td>`;
          }
          return `<td>${convertMarkdownToHtml(cell?.td || '')}</td>`;
        }).join('') + '</tr>'
      ).join('') + '</tbody>\n';
    }
  }

  return html + '</table>\n</div>\n';
};

const processTableCellContent = (cellContent) => {
  if (!cellContent) return '';

  // Split by line breaks to handle multi-line content
  const lines = cellContent.split('\n');
  const processedLines = [];

  for (const line of lines) {
    const trimmedLine = line.trim();
    if (!trimmedLine) continue;

    // Process the line for input variables
    const processedLine = processCellLine(trimmedLine);
    if (processedLine) {
      processedLines.push(processedLine);
    }
  }

  // Join lines with <br> tags for proper HTML formatting
  if (processedLines.length === 1) {
    return processedLines[0];
  } else {
    return processedLines.join('<br>');
  }
};

const processCellLine = (line) => {
  if (!line) return '';

  // Split by variable patterns while preserving them
  const parts = line.split(/(\{\{[^}]+\}\})/);

  const processedParts = [];
  for (const part of parts) {
    if (part.startsWith('{{') && part.endsWith('}}')) {
      // Convert input variable to HTML element
      const inputHtml = convertInputVariableToHtml(part);
      processedParts.push(inputHtml);
    } else {
      // Apply markdown processing to regular text
      processedParts.push(convertMarkdownToHtml(part));
    }
  }

  return processedParts.join('');
};

const convertInputVariableToHtml = (variable) => {
  // Remove the {{ }} wrapper
  const variableContent = variable.replace(/[{}]/g, '').trim();

  // Determine input type based on variable name
  if (variableContent.includes('input_checkbox')) {
    return '<input type="checkbox" style="margin: 0 4px; vertical-align: middle;">';
  } else if (variableContent.includes('input_number_field')) {
    return '<input type="number" style="width: 80px; padding: 2px 4px; border: 1px solid #ccc; border-radius: 2px;">';
  } else if (variableContent.includes('input_text_field')) {
    return '<input type="text" style="width: 120px; padding: 2px 4px; border: 1px solid #ccc; border-radius: 2px;">';
  } else if (variableContent.includes('input_text_area')) {
    return '<textarea style="width: 120px; height: 40px; padding: 2px 4px; border: 1px solid #ccc; border-radius: 2px; resize: vertical;"></textarea>';
  } else if (variableContent.includes('input_select')) {
    return '<select style="width: 120px; padding: 2px 4px; border: 1px solid #ccc; border-radius: 2px;"><option value="">Select...</option></select>';
  } else {
    // For unknown variables, keep as-is
    return variable;
  }
};
  
export const convertNumberedListItemToHtml = (item, itemIndex, level, parentNumbers) => {
    const numberString = [...parentNumbers, itemIndex + 1].join('.') + '.';
    let html = `<li class="list-item"><span class="item-text">${convertTextWithCheckboxesToHtml(item.text)}</span>`;
  
    if (item.children && level <= 4) {
      html += '<div class="sub-list">';
      item.children.forEach((child, childIndex) => {
        if (child.type === 'table') {
          html += convertTableToHtml(child);
        } else if (['ol', 'ul'].includes(child.type)) {
          html += child.type === 'ol'
            ? convertNumberedListItemToHtml(child, childIndex, level + 1, [...parentNumbers, itemIndex + 1])
            : convertUnorderedListItemToHtml(child);
        } else {
          html += `<div class="sub-item"><span class="sub-text">${
            child.content ? convertMixedContentToHtml(child.content) : convertTextWithCheckboxesToHtml(child.text)}</span></div>`;
        }
      });
      html += '</div>';
    }
  
    return html + '</li>';
  };
  
export const convertUnorderedListItemToHtml = (item) => {
    let html = '<ul class="unordered-list">';
    if (item.items) {
      html += item.items.map(i => `<li class="list-item">${convertTextWithCheckboxesToHtml(i.text)}</li>`).join('');
    }
    return html + '</ul>';
  };
  
export const convertSectionToHtml = (section) => {
  const tag = {
    h1: 'h1',
    h2: 'h2',
    p: 'p'
  }[section.type] || 'div';

  if (section.type === 'ol') {
    return '<ol class="numbered-list">' +
      (section.items || []).map((item, i) => convertNumberedListItemToHtml(item, i, 1, [])).join('') +
      '</ol>';
  }

  // Check if section has items (for tables and other structured content)
  if (section.items && Array.isArray(section.items)) {
    let itemsHtml = '';
    for (const item of section.items) {
      // Check if item is a table (has 'head' and 'rows')
      if (item.head && item.rows) {
        itemsHtml += convertTableToHtml(item);
      } else {
        // Handle other item types
        itemsHtml += convertTextWithCheckboxesToHtml(item.text || '');
      }
    }
    return `<${tag} class="section-${tag}">${itemsHtml}</${tag}>`;
  }

  return `<${tag} class="section-${tag}">` +
    (section.content ? convertMixedContentToHtml(section.content) : convertTextWithCheckboxesToHtml(section.text)) +
    `</${tag}>`;
};
  
export const convertJsonToHtml = (jsonString) => {
    try {
      const fixedJson = validateAndFixJson(jsonString);
      const jsonData = JSON.parse(fixedJson);
  
      let html = '<div class="contract-form">';
      html += (jsonData.sections || []).map(convertSectionToHtml).join('');
      html += '</div>';
  
      return { success: true, html, data: jsonData };
    } catch (error) {
      const message = error.message || 'Unknown JSON parsing error';
      return { success: false, message };
    }
  };
  
export const convertJsonToReact = (jsonString) => {
    return convertJsonToHtml(jsonString);
  };
  